import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import style from './index.module.scss';
import { getDaysOfMonth } from 'page/MeetingSchedule/utils/timeUtils';
import moment, { Moment } from 'moment';

import CalendarItem from './Calendaritem';
import { useScheduleContext } from 'page/MeetingSchedule/Provider/ScheduleProvider';
import { useResizeCalendar } from './hook/useResizeCalendar';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'model';
import { getMeetingCalendarDetailList } from 'model/meeting';
import { Spin } from 'antd';
import { SEEWO_IM_MEETING_SCHEDULE_CHANGED_EVENT } from 'lib/seewoIm';
import { useActivate, useUnactivate } from 'react-activation';

interface IProps {
  taskList?: number[];
  noticeList?: number[];
  binkTime?: Moment | undefined;
  onSwitch?: (startTime: number, endTime: number) => void;
  onSelectDate?: (date: number) => void;
}

const CROSS_DAY_REFRESH_RANDOM_WINDOW = 5 * 60 * 1000;

const getCrossDayRefreshDelay = () => {
  const now = moment();
  return now.clone().add(1, 'day').startOf('day').diff(now) + Math.floor(Math.random() * CROSS_DAY_REFRESH_RANDOM_WINDOW);
};

const ScheduleView: React.FC<IProps> = ({ binkTime }) => {
  const { originState } = useScheduleContext();
  const { calendarMonth } = originState;
  const { width, height, maxCountItem } = useResizeCalendar();
  const calendarList = useSelector((state: RootState) => state.meeting.meetingCalendarDetailList);
  const calendarLoading = useSelector((state: RootState) => state.meeting.meetingCalendarLoading);
  const dispatch = useDispatch();
  const crossDayRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 周
  const weekTitles = useMemo(() => {
    return [...Array(7)].map((_, weekInx) => {
      return moment().day(weekInx);
    });
  }, []);

  // 天
  const days = useMemo(() => {
    return getDaysOfMonth(calendarMonth.year(), calendarMonth.month() + 1);
  }, [calendarMonth]);

  const fetchScheduleList = useCallback((silent = false) => {
    const targetDay = getDaysOfMonth(calendarMonth.year(), calendarMonth.month() + 1);
    const beginTime = targetDay[0].valueOf();
    const endTime = targetDay[targetDay.length - 1].add(1, 'day').valueOf();
    dispatch(
      getMeetingCalendarDetailList({
        beginTime,
        endTime,
        silent
      })
    );
  }, [days]);

  const getMeetingList = (index: number) => {
    if (!!calendarList[index] && Array.isArray(calendarList[index].scheduleList)) {
      return calendarList[index].scheduleList;
    }
    return [];
  };

  const clearCrossDayRefreshTimer = () => {
    if (crossDayRefreshTimerRef.current) {
      clearTimeout(crossDayRefreshTimerRef.current);
      crossDayRefreshTimerRef.current = null;
    }
  };

  const scheduleCrossDayRefresh = () => {
    clearCrossDayRefreshTimer();
    crossDayRefreshTimerRef.current = setTimeout(() => {
      fetchScheduleList(true);
      scheduleCrossDayRefresh();
    }, getCrossDayRefreshDelay());
  };

  useEffect(() => {
    fetchScheduleList();
  }, [fetchScheduleList]);

  useEffect(() => {
    const handleMeetingScheduleChanged = () => {
      fetchScheduleList(true);
    };

    window.addEventListener(SEEWO_IM_MEETING_SCHEDULE_CHANGED_EVENT, handleMeetingScheduleChanged);
    return () => {
      window.removeEventListener(SEEWO_IM_MEETING_SCHEDULE_CHANGED_EVENT, handleMeetingScheduleChanged);
    };
  }, [fetchScheduleList]);

  useEffect(() => {
    scheduleCrossDayRefresh();
    return clearCrossDayRefreshTimer;
  }, [fetchScheduleList]);

  useActivate(() => {
    scheduleCrossDayRefresh();
  });

  useUnactivate(() => {
    clearCrossDayRefreshTimer();
  });

  useEffect(() => {}, []);

  return (
    <div className={style.warp}>
      <div style={{ width: `${width}%` }} className={style.scheduleWarp}>
        <div className={style.calendar}>
          <div className={style.calendarTitle}>
            {weekTitles.map((title) => {
              return <div className={style.calendarWeek}>{title.format('ddd')}</div>;
            })}
          </div>
          <Spin spinning={calendarLoading}>
            <div style={{ height: `${height}px` }} className={style.calendarContent}>
              {days.map((day, index) => {
                return (
                  <div style={{ height: `${height / 6}px` }} className={style.calendarDay}>
                    <CalendarItem
                      meetingList={getMeetingList(index)}
                      maxNumberPayload={maxCountItem}
                      itemTime={day}
                      key={day.valueOf()}
                      binkTime={binkTime}
                      index={index}
                    ></CalendarItem>
                  </div>
                );
              })}
            </div>
          </Spin>
        </div>
      </div>
    </div>
  );
};
export default ScheduleView;
